import React, { useState } from 'react';
import { Scale, ArrowLeft, Lightbulb, Users, CheckCircle, Phone, Mail, Star, X } from 'lucide-react';

function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navHeight = 80; // Height of fixed navigation
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const openContactModal = () => {
    setIsModalOpen(true);
  };

  const closeContactModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="font-sans" dir="rtl">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">דרור אורן</h1>
                <p className="text-sm text-slate-600">משרד עורכי דין</p>
              </div>
            </div>
            <div className="hidden md:flex gap-8">
              <button 
                onClick={() => scrollToSection('hero')}
                className="text-slate-700 hover:text-amber-600 transition-colors cursor-pointer"
              >
                בית
              </button>
              <button 
                onClick={() => scrollToSection('about')}
                className="text-slate-700 hover:text-amber-600 transition-colors cursor-pointer"
              >
                אודות
              </button>
              <button 
                onClick={() => scrollToSection('services')}
                className="text-slate-700 hover:text-amber-600 transition-colors cursor-pointer"
              >
                תחומי התמחות
              </button>
              <button 
                onClick={openContactModal}
                className="text-slate-700 hover:text-amber-600 transition-colors cursor-pointer"
              >
                צור קשר
              </button>
            </div>
            <button 
              onClick={openContactModal}
              className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md transition-colors cursor-pointer"
            >
              יצירת קשר
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="pt-20 pb-16 px-6 min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-amber-200 text-amber-700 bg-amber-50">
                  משרד ותיק בן 30 שנה
                </div>
                <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                  דרור אורן
                  <span className="block text-amber-600">משרד עו״ד</span>
                </h1>
                <p className="text-2xl text-slate-600 font-medium">
                  שירות, מקצועיות ופתרונות יצירתיים
                </p>
                <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
                  משרד בוטיק ותיק הפועל כ-30 שנה ומתמחה בתחום הנדל״ן על שלל היבטיו. גישה יצירתית ולא קונבנציונלית לפתרון בעיות משפטיות מורכבות.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={openContactModal}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg rounded-md font-medium inline-flex items-center gap-2 cursor-pointer"
                >
                  קבעו פגישת ייעוץ
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => scrollToSection('about')}
                  className="border border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-lg rounded-md font-medium cursor-pointer"
                >
                  למידע נוסף
                </button>
              </div>
              <div className="flex items-center gap-8 pt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900">30+</div>
                  <div className="text-slate-600">שנות ניסיון</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900">1000+</div>
                  <div className="text-slate-600">עסקאות</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-900">95%</div>
                  <div className="text-slate-600">שיעור הצלחה</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="משרד עורכי דין המתמחה בנדלן" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              <div className="absolute -top-4 -right-4 w-full h-full bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">אודות המשרד</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              30 שנות מומחיות ויצירתיות בעולם הנדל״ן והמקרקעין
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-slate-900">עו״ד דרור אורן</h3>
              <p className="text-lg text-slate-600 leading-relaxed">
                משרד עו"ד דרור אורן הוא משרד בוטיק ותיק, הפועל כ-30 שנה ומתמחה בתחום הנדל״ן על שלל היבטיו – עסקאות מורכבות, מיסוי מקרקעין, היטלי השבחה ועוד.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                המשרד ידוע בגישתו היצירתית והלא קונבנציונלית לפתרון בעיות משפטיות, תוך מתן מענה אישי, מדויק וחדשני ללקוחות פרטיים, יזמים, קבלנים וגופים עסקיים.
                עו״ד אורן משמש בורר במינוי יו״ר לשכת עורכי הדין.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                  <Lightbulb className="w-12 h-12 text-amber-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-slate-900">גישה יצירתית</h4>
                  <p className="text-sm text-slate-600 mt-2">פתרונות לא קונבנציונליים לבעיות מורכבות</p>
                </div>
                <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                  <Users className="w-12 h-12 text-amber-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-slate-900">מענה אישי</h4>
                  <p className="text-sm text-slate-600 mt-2">ליווי צמוד ואישי לכל לקוח</p>
                </div>
                <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                  <CheckCircle className="w-12 h-12 text-amber-600 mx-auto mb-3" />
                  <h4 className="font-semibold text-slate-900">30 שנות ניסיון</h4>
                  <p className="text-sm text-slate-600 mt-2">מומחיות מוכחת בתחום הנדל״ן</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="פגישת ייעוץ נדלן" 
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">תחומי התמחות</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              מעטפת משפטית מלאה ומתמחה בכל היבטי עולם הנדל״ן והמקרקעין
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                emoji: "🤝",
                title: "ליווי עסקאות מקרקעין",
                description: "ליווי מקצועי ומקיף של עסקאות מורכבות, החל מהבדיקות המקדמיות ועד להשלמת העסקה בהצלחה."
              },
              {
                emoji: "💰",
                title: "מיסוי מקרקעין",
                description: "תכנון מס מיטבי ומקצועי, ייעוץ מתקדם במס שבח, מס רכישה ותכנון מס אסטרטגי."
              },
              {
                emoji: "📊",
                title: "היטל השבחה",
                description: "טיפול מקצועי בהיטלי השבחה, הגשת התנגדויות ומיצוי כל האפשרויות להקלות."
              },
              {
                emoji: "🏗️",
                title: "תמ״א 38",
                description: "ליווי מקצועי של דיירים ויזמים בפרויקטי התחדשות עירונית ותמ״א 38."
              },
              {
                emoji: "⚖️",
                title: "פירוק שיתוף",
                description: "טיפול מקצועי בהליכי פירוק שיתוף ויישוב סכסוכים בין שותפים במקרקעין."
              },
              {
                emoji: "💡",
                title: "פתרון בעיות מורכבות",
                description: "גישה יצירתית ולא קונבנציונלית לפתרון בעיות משפטיות מורכבות בתחום המקרקעין."
              }
            ].map((service, index) => (
              <div key={index} className="h-full hover:shadow-xl transition-all duration-300 border-0 shadow-md group rounded-lg bg-white">
                <div className="p-8">
                  <div className="text-4xl mb-4">{service.emoji}</div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-amber-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">מה הלקוחות אומרים</h2>
            <p className="text-xl text-slate-600">חוות דעת אמיתיות מלקוחות מרוצים</p>
          </div>
          <div className="relative">
            <div className="rounded-lg border-0 shadow-xl bg-white">
              <div className="p-12 text-center">
                <div className="flex justify-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 text-amber-400 fill-current" />
                  ))}
                </div>
                <p className="text-xl text-slate-700 mb-6 leading-relaxed italic">
                  "מומחה אמיתי בהתחדשות עירונית. הצליח להוביל פרויקט תמ״א 38 מורכב בבניין שלנו להצלחה, לשביעות רצון כל הדיירים."
                </p>
                <h4 className="font-bold text-slate-900 text-lg">ועד הבית, רחוב הפלמ״ח 12</h4>
              </div>
            </div>
            <div className="flex justify-center mt-6 gap-2">
              <button className="w-3 h-3 rounded-full transition-colors bg-slate-300"></button>
              <button className="w-3 h-3 rounded-full transition-colors bg-slate-300"></button>
              <button className="w-3 h-3 rounded-full transition-colors bg-amber-600"></button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">צרו קשר</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              נשמח לעמוד לרשותכם ולהעניק לכם ייעוץ משפטי מקצועי
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-6">פרטי התקשרות</h3>
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">טלפון</h4>
                      <p className="text-slate-600" dir="ltr">054-440-0405</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">אימייל</h4>
                      <a 
                        href="mailto:dror@droren.co.il"
                        className="text-slate-600 hover:text-amber-600 transition-colors no-underline"
                        dir="ltr"
                      >
                        dror@droren.co.il
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-slate-50 p-6 rounded-xl">
                <h4 className="font-semibold text-slate-900 mb-3">שעות פעילות</h4>
                <div className="space-y-2 text-slate-600">
                  <div className="flex justify-between">
                    <span>ראשון - חמישי</span>
                    <span>09:00 - 18:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>שישי</span>
                    <span>09:00 - 13:00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>שבת</span>
                    <span>סגור</span>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="rounded-lg border-0 shadow-xl bg-white">
                <div className="p-8 text-center">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6">יצירת קשר</h3>
                  <p className="text-lg text-slate-600 mb-8">
                    לפגישת ייעוץ או לכל שאלה, צרו קשר ונחזור אליכם בהקדם
                  </p>
                  <button 
                    onClick={openContactModal}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white py-4 text-lg rounded-md font-medium transition-colors inline-flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Mail className="w-5 h-5" />
                    פרטי יצירת קשר
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
                  <Scale className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">דרור אורן</h3>
                  <p className="text-slate-400">משרד עורכי דין</p>
                </div>
              </div>
              <p className="text-slate-400">
                שירות, מקצועיות ופתרונות יצירתיים - המשרד שלכם לכל צורך משפטי בתחום הנדל״ן.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">קישורים</h4>
              <div className="space-y-2">
                <button 
                  onClick={() => scrollToSection('about')}
                  className="block text-slate-400 hover:text-amber-400 transition-colors cursor-pointer"
                >
                  אודות
                </button>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="block text-slate-400 hover:text-amber-400 transition-colors cursor-pointer"
                >
                  תחומי התמחות
                </button>
                <button 
                  onClick={openContactModal}
                  className="block text-slate-400 hover:text-amber-400 transition-colors cursor-pointer"
                >
                  צור קשר
                </button>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">פרטי קשר</h4>
              <div className="space-y-2 text-slate-400">
                <p dir="ltr">054-440-0405</p>
                <a 
                  href="mailto:dror@droren.co.il"
                  className="block hover:text-amber-400 transition-colors no-underline"
                  dir="ltr"
                >
                  dror@droren.co.il
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 mt-8 text-center text-slate-400">
            <p>© 2025 דרור אורן, משרד עורכי דין. כל הזכויות שמורות.</p>
          </div>
                  </div>
        </footer>

        {/* Contact Modal */}
        {isModalOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={closeContactModal}
          >
            <div 
              className="bg-white rounded-lg max-w-md w-full p-8 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                onClick={closeContactModal}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-amber-600" />
                </div>
                
                <h3 className="text-2xl font-bold text-slate-900 mb-4">יצירת קשר</h3>
                
                <p className="text-slate-600 mb-6">
                  לקביעת פגישת ייעוץ או לכל שאלה, שלחו אימייל לכתובת:
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Mail className="w-5 h-5 text-amber-600" />
                    <span className="font-semibold text-slate-700">אימייל</span>
                  </div>
                  <a 
                    href="mailto:dror@droren.co.il"
                    className="text-xl font-bold text-amber-600 hover:text-amber-700 transition-colors no-underline"
                    dir="ltr"
                  >
                    dror@droren.co.il
                  </a>
                </div>
                
                <div className="text-sm text-slate-500 mb-4">
                  או התקשרו:
                </div>
                
                <div className="flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600" dir="ltr">054-440-0405</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

export default App; 
